import { DndContext, DragEndEvent } from "@dnd-kit/core";
import { SortableContext } from "@dnd-kit/sortable";
import { zodResolver } from "@hookform/resolvers/zod";
import { PenLine, Trash2 } from "lucide-react";
import React, { useState } from "react";
import {
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
} from "react-hook-form";
import { useDispatch } from "react-redux";

import AddOption from "./AddOption";
import DefaultColors from "./DefaultColors";

import FormInput from "@/components/form-fields/form-input";
import { DragableObject } from "@/components/reusables/Dnd/DragableObject";
import FormWrapper from "@/components/reusables/custom-form-fields/FormWrapper";
import { Button } from "@/components/ui/button";
import { ColorPicker } from "@/components/ui/color-picker";
import { Form, FormField } from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { TWENTY } from "@/constants/constants";
import { DELETE_CONFIRMATION_CONSTANTS } from "@/constants/global-constants";
import { NewTOptions } from "@/interfaces/interfaces";
import { cn, formatToCapitalizedWords } from "@/lib/utils";
import { showAlertDialog } from "@/redux/slices/AlertSlice";
import {
  AllForOptions,
  NewEstimateOptions,
  TNewEstimateOptions,
} from "@/schemas/GlobalSchema";
import { TEstimateOption } from "@/schemas/project-register.schema";

type TOptionsSheet<T> = {
  title?: string;
  isOpen: {
    isOpen: boolean;
    statusFor: string;
  };
  toggleOpen: () => void;
  optionList: NewTOptions<T>[];
  optionFor: string;
  actions: {
    addMutation: (data: NewTOptions<T>) => Promise<void>;
    deleteMutation: (idx: string) => void;
    singleEdit?: (idx: string, data: NewTOptions<T>) => void;
    editMutation: (data: TNewEstimateOptions) => Promise<void>;
  };
};

const EditPopover = ({
  idx,
  children,
}: {
  idx: number;
  children: React.ReactNode;
}): React.JSX.Element => {
  const form = useFormContext<TEstimateOption>();
  const [custom, setCustom] = useState(false);

  return (
    <Popover>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className='min-w-[450px] space-y-4 '>
        <FormInput
          form={form}
          name={`data.${idx}.name`}
          label='Label Name'
          required
          charLimit={TWENTY}
        />
        <div>
          <Label className='mb-1 font-normal' required>
            Colors
          </Label>
          <DefaultColors
            colorHandler={(color: string) => {
              form.setValue(`data.${idx}.details.textColor`, color, {
                shouldDirty: true,
              });
              form.setValue(`data.${idx}.details.backgroundColor`, color, {
                shouldDirty: true,
              });
              form.setValue(`data.${idx}.details.textOpacity`, 100, {
                shouldDirty: true,
              });
              form.setValue(`data.${idx}.details.backgroundOpacity`, 12, {
                shouldDirty: true,
              });
            }}
            activeColor={form.watch(`data.${idx}.details.textColor`)}
          />
        </div>
        <div className='flex items-center justify-between'>
          <Label>Custom</Label>
          <Switch checked={custom} onCheckedChange={() => setCustom(!custom)} />
        </div>
        {custom && (
          <div className='grid grid-cols-2 gap-4'>
            <div className='p-2 border rounded-md'>
              <FormField
                name={`data.${idx}.details.textColor`}
                control={form.control}
                render={({ field }) => (
                  <FormWrapper label='Text'>
                    <ColorPicker
                      onChange={(v) => {
                        form.setValue(`data.${idx}.details.textColor`, v, {
                          shouldDirty: true,
                        });
                      }}
                      onOpacityChange={(v) => {
                        form.setValue(`data.${idx}.details.textOpacity`, v, {
                          shouldDirty: true,
                        });
                      }}
                      value={{
                        color: field.value,
                        opacity: form.watch(`data.${idx}.details.textOpacity`),
                      }}
                    />
                  </FormWrapper>
                )}
              />
            </div>

            <div className='p-2 border rounded-md'>
              <FormField
                name={`data.${idx}.details.backgroundColor`}
                control={form.control}
                render={({ field }) => (
                  <FormWrapper label='Background'>
                    <ColorPicker
                      onChange={(v) => {
                        form.setValue(
                          `data.${idx}.details.backgroundColor`,
                          v,
                          {
                            shouldDirty: true,
                          },
                        );
                      }}
                      onOpacityChange={(v) => {
                        form.setValue(
                          `data.${idx}.details.backgroundOpacity`,
                          v,
                          {
                            shouldDirty: true,
                          },
                        );
                      }}
                      value={{
                        color: field.value,
                        opacity: form.watch(
                          `data.${idx}.details.backgroundOpacity`,
                        ),
                      }}
                    />
                  </FormWrapper>
                )}
              />
            </div>
          </div>
        )}
        {/* {form.formState.errors.details && (
          <p className='text-xs text-red-500'>
            {handleDetailsErrors(form.formState.errors)}
          </p>
        )} */}
      </PopoverContent>
    </Popover>
  );
};

const OptionsSheet = <T extends AllForOptions>({
  title,
  isOpen,
  toggleOpen,
  optionList,
  optionFor,
  actions,
}: TOptionsSheet<T>): React.JSX.Element => {
  const [isAddOption, setIsAddOption] = useState<boolean>(false);

  const form = useForm<TNewEstimateOptions>({
    resolver: zodResolver(NewEstimateOptions),
    values: { data: optionList || [] },
  });

  const { fields, move } = useFieldArray({
    control: form.control,
    name: "data",
    keyName: "formId",
  });

  const handleReorder = (e: DragEndEvent): void => {
    if (!e.over) {
      return;
    }

    const oldIndex = fields.findIndex((field) => field.id === e.active.id);
    const newIndex = fields.findIndex((field) => field.id === e.over!.id);

    if (oldIndex !== newIndex) {
      move(oldIndex, newIndex);

      if (oldIndex !== newIndex) {
        const data = form.watch("data");

        // Update positions after reordering
        const updatedFields = data.map((field, index) => ({
          ...field,
          details: {
            ...field.details,
            position: index,
          },
        }));

        // Update the form data with new positions
        form.setValue("data", updatedFields);
      }
    }
  };

  const onSubmit = async (data: TNewEstimateOptions): Promise<void> => {
    const dataToSend = data?.data.map((d, idx) => {
      return {
        ...d,
        details: {
          ...d.details,
          position: idx,
        },
      };
    });

    await actions.editMutation({
      data: dataToSend,
    });
  };

  const dispatch = useDispatch();
  const handleDelete = (idx: string): void => {
    dispatch(
      showAlertDialog({
        title: DELETE_CONFIRMATION_CONSTANTS.title,
        desc: "If you delete this you cannot restore this option",
        btnName: DELETE_CONFIRMATION_CONSTANTS.btnName,
        cancelName: DELETE_CONFIRMATION_CONSTANTS.cancelName,
        btnAction: async () => {
          await actions.deleteMutation(idx);
        },
      }),
    );
  };

  const handleExit = (): void => {
    // resetAll();
    form.reset();
    toggleOpen();
  };

  return (
    <>
      <Sheet open={isOpen.isOpen} onOpenChange={handleExit}>
        <SheetContent className='min-w-[30dvw] overflow-y-auto min-h-full'>
          <SheetHeader>
            <div className='flex flex-wrap items-center justify-between gap-2'>
              <SheetTitle className='text-xl font-normal capitalize'>
                Edit {title || formatToCapitalizedWords(optionFor)}
              </SheetTitle>
              <Button onClick={() => setIsAddOption(!isAddOption)}>
                Add {title || formatToCapitalizedWords(optionFor)}
              </Button>
            </div>
          </SheetHeader>
          <Form {...form}>
            <FormProvider {...form}>
              <form
                className='h-[95%] flex flex-col justify-between'
                onSubmit={form.handleSubmit(onSubmit)}
              >
                <div>
                  <DndContext onDragEnd={handleReorder}>
                    <SortableContext items={fields.map((field) => field.id!)}>
                      {[...form.watch("data")]
                        .sort(
                          (a, b) => a.details?.position - b.details?.position,
                        )
                        .map((s, idx) => {
                          const backgroundOpacity =
                            s?.details?.backgroundOpacity === 100
                              ? "FF"
                              : s?.details?.backgroundOpacity;

                          const bgWithOpacity =
                            (s?.details?.backgroundColor ?? "") +
                            backgroundOpacity;

                          return (
                            <DragableObject
                              key={s.id}
                              id={s.id || ""}
                              className='my-2'
                              isCenter
                            >
                              <div className='flex justify-between w-full p-3 group hover:bg-slate-100 hover:rounded-md'>
                                <Button
                                  className={cn("min-w-[180px] text-center")}
                                  variant={"outline"}
                                  style={{
                                    backgroundColor: bgWithOpacity,
                                  }}
                                >
                                  <span
                                    className='line-clamp-1'
                                    style={{
                                      opacity:
                                        (s?.details?.textOpacity || 100) / 100,
                                      color: s?.details?.textColor || "#000",
                                    }}
                                  >
                                    {s.name || "Select Option"}
                                  </span>
                                </Button>

                                <div className='flex items-center invisible gap-2  group-hover:visible'>
                                  <EditPopover idx={idx}>
                                    <PenLine
                                      className='hover:cursor-pointer'
                                      size={20}
                                    />
                                  </EditPopover>
                                  <Trash2
                                    className='hover:cursor-pointer'
                                    color='red'
                                    size={20}
                                    onClick={() => s?.id && handleDelete(s.id)}
                                  />
                                </div>
                              </div>
                            </DragableObject>
                          );
                        })}
                    </SortableContext>
                  </DndContext>
                </div>

                {form.formState.isDirty && (
                  <SheetFooter>
                    <Button type='submit'>Save</Button>
                    <Button variant='secondary' onClick={handleExit}>
                      Cancel
                    </Button>
                  </SheetFooter>
                )}
              </form>
            </FormProvider>
          </Form>
        </SheetContent>
      </Sheet>

      <AddOption
        title={title}
        isOpen={isAddOption}
        toggleModal={() => setIsAddOption(!isAddOption)}
        optionFor={isOpen?.statusFor as AllForOptions}
        position={fields.length + 1}
        addOption={actions.addMutation}
      />
    </>
  );
};

export default OptionsSheet;
